/**
 * CorrelationContext
 *
 * Frontend half of end-to-end trace propagation. A single correlation
 * id (`ttx_<uuid>`) is generated when the user starts a CoPilot flow
 * AND when major lifecycle events fire (restart / retake / discard).
 * Every subsequent log call within that scope picks the id up via
 * `useCopilotCorrelationId`, the SDK's HTTP interceptor stamps it on
 * outbound requests as `x-trace-id`, and platform-api's middleware
 * threads it into its own logger context. The result: a QA engineer
 * pastes one id into the Sentry / PostHog / log-search box and gets
 * the user's whole journey across services.
 *
 * Pre-2026-04-28 the suite generated NO correlation id at the
 * frontend boundary — the audit found 0 matches for `x-correlation-
 * id` / `x-trace-id` propagation in journey-web. Sentry events from
 * the FE and backend logs from the same user action lived as separate
 * islands; QA had to correlate by `(flowId, journeyId, timestamp)`
 * tuples which is fragile.
 *
 * Lifecycle:
 *   • Provider mounts at the host page level (CheckUpAssessmentPage,
 *     etc.) with `flowId` + `journeyId` defaults. It does NOT
 *     auto-generate a correlation id on mount — flows produce too
 *     many ids if every mount creates one. Instead, callers stamp a
 *     fresh id when starting a meaningful action (start flow, restart,
 *     finalize) via `setCorrelationId`.
 *   • The SDK interceptor reads the value via the registered getter
 *     (`registerCorrelationIdGetter`) from `client/sdk-correlation.ts`.
 *   • If no id has been stamped, the interceptor generates a
 *     per-request id — still useful but not linked across calls.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { generateTraceId } from '@tonnie/observability';

interface CorrelationContextValue {
  correlationId: string | undefined;
  /**
   * Stamp a fresh correlation id for the next user action. Returns
   * the new id so callers can use it directly without waiting for a
   * re-render. Call at the start of a logical "action": flow start,
   * restart confirm, retake confirm, finalize.
   */
  beginAction(label?: string): string;
  /**
   * Clear the active id — typically on flow exit / completion. The
   * next `beginAction` will mint a fresh one. Optional; the next
   * `beginAction` overrides regardless.
   */
  clear(): void;
}

const CorrelationContext = createContext<CorrelationContextValue | null>(null);

interface CopilotCorrelationProviderProps {
  /**
   * Optional initial id. Useful for tests; production code starts
   * undefined and stamps a fresh id at the first action.
   */
  initialId?: string;
  /** When true, `beginAction` calls log to the console for QA tracing in dev. */
  debug?: boolean;
  children: ReactNode;
}

export function CopilotCorrelationProvider({
  initialId,
  debug,
  children,
}: CopilotCorrelationProviderProps) {
  const [correlationId, setCorrelationId] = useState<string | undefined>(initialId);
  // Mirror the live value in a ref so the SDK interceptor's
  // synchronous `getCorrelationId()` getter (registered below) returns
  // the most recent id even between renders.
  const ref = useRef<string | undefined>(initialId);

  const beginAction = useCallback(
    (label?: string) => {
      const id = generateTraceId();
      ref.current = id;
      setCorrelationId(id);
      if (debug && import.meta.env?.DEV) {
        // eslint-disable-next-line no-console -- dev-only trace; this provider feeds the logger, so it cannot log through it
        console.debug('[CP:Correlation] begin', { id, label });
      }
      return id;
    },
    [debug]
  );

  const clear = useCallback(() => {
    ref.current = undefined;
    setCorrelationId(undefined);
  }, []);

  // Register the SDK-side getter on mount so the HTTP client picks
  // the live value. Unregister on unmount so we don't leak the
  // closure if the provider is replaced.
  useEffect(() => {
    registerCorrelationIdGetter(() => ref.current);
    return () => {
      registerCorrelationIdGetter(null);
    };
  }, []);

  const value = useMemo<CorrelationContextValue>(
    () => ({ correlationId, beginAction, clear }),
    [correlationId, beginAction, clear]
  );

  return <CorrelationContext.Provider value={value}>{children}</CorrelationContext.Provider>;
}

/**
 * Read the active correlation id + the action-stamping callbacks.
 * Returns a stub when the provider isn't mounted (rare — provider is
 * mounted at the app root). The stub keeps logs flowing without an
 * id rather than crashing.
 */
export function useCopilotCorrelation(): CorrelationContextValue {
  const ctx = useContext(CorrelationContext);
  if (!ctx) {
    return {
      correlationId: undefined,
      beginAction: () => generateTraceId(),
      clear: () => undefined,
    };
  }
  return ctx;
}

/**
 * Convenience selector for log call sites that only need the id.
 */
export function useCopilotCorrelationId(): string | undefined {
  return useCopilotCorrelation().correlationId;
}

// ─── SDK getter registry (module-level singleton) ────────────────────

/**
 * Module-level pointer the SDK interceptor reads. We don't import
 * the React tree from the SDK — the SDK is framework-agnostic. The
 * provider above writes the getter; the interceptor reads it.
 *
 * Multiple providers in a single app are not supported. If two
 * providers mount, the second overrides the first's getter — which
 * is fine because every `beginAction` call updates the ref the
 * winning getter reads.
 */
let activeGetter: (() => string | undefined) | null = null;

export function registerCorrelationIdGetter(getter: (() => string | undefined) | null): void {
  activeGetter = getter;
}

export function getActiveCorrelationId(): string | undefined {
  return activeGetter?.();
}
